import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendChatMessage, type ChatTurn, type ChatMessage } from '../lib/api.js';

export default function ChatView() {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, loading]);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setTurns((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const history: ChatMessage[] = [
        ...turns.map((t) => ({ role: t.role, content: t.content })),
        { role: 'user', content: text },
      ];
      const { answer, sources } = await sendChatMessage(history);
      setTurns((prev) => [...prev, { role: 'assistant', content: answer, sources }]);
    } catch {
      setTurns((prev) => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen max-w-3xl mx-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700 flex items-center gap-3">
        <span className="text-xl">💬</span>
        <div>
          <div className="font-semibold text-gray-100">Ask your insights</div>
          <div className="text-xs text-gray-500">Claude Haiku · answers grounded in your collected insights</div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {turns.length === 0 && (
          <div className="text-center text-gray-500 mt-16 space-y-2">
            <div className="text-4xl">🧠</div>
            <p className="text-sm">Ask anything about Claude Code and AI development.</p>
            <p className="text-xs text-gray-600">e.g. "What hooks have been recommended?" or "How do I configure memory across sessions?"</p>
          </div>
        )}

        {turns.map((turn, i) => (
          <div key={i} className={`flex flex-col gap-2 ${turn.role === 'user' ? 'items-end' : 'items-start'}`}>
            <div
              className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                turn.role === 'user'
                  ? 'bg-obsidian-accent text-white rounded-br-sm'
                  : 'card rounded-bl-sm'
              }`}
            >
              {turn.content}
            </div>
            {turn.sources && turn.sources.length > 0 && (
              <div className="flex flex-wrap gap-2 max-w-[80%]">
                {turn.sources.slice(0, 4).map((s) => (
                  <button
                    key={s.insightId}
                    onClick={() => navigate(`/insight/${s.insightId}`)}
                    className="text-xs bg-gray-800 border border-gray-700 hover:border-obsidian-accent px-2 py-1 rounded text-gray-400 hover:text-gray-200 transition-colors text-left"
                  >
                    📄 {s.title.length > 40 ? s.title.slice(0, 40) + '…' : s.title}
                    <span className="text-gray-600 ml-1">· {s.relevanceScore}/10</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex items-start">
            <div className="card px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-obsidian-accent animate-pulse"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-gray-700">
        <div className="flex gap-3 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder="Ask a question about Claude Code..."
            rows={1}
            className="input flex-1 resize-none min-h-[40px] max-h-32"
          />
          <button
            onClick={() => void handleSend()}
            disabled={!input.trim() || loading}
            className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}
